/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { AVAILABLE_LAYOUTS } from '../settings';

type Orientation = 'LR' | 'RL' | 'TB' | 'BT';

type Props = {
  cy: any;                    // cytoscape.Core | null
  initialLayoutName?: string; // z.B. renderData.config.layout

  // 👉 optionaler Controlled-Modus (neu, minimal-invasiv)
  currentLayoutName?: string;
  onLayoutChange?: (name: string) => void;
};

const ORIENTATIONS: { key: Orientation; label: string; title: string }[] = [
  { key: 'LR', label: '→', title: 'Links → Rechts' },
  { key: 'RL', label: '←', title: 'Rechts → Links' },
  { key: 'TB', label: '↓', title: 'Oben → Unten' },
  { key: 'BT', label: '↑', title: 'Unten → Oben' },
];

// Nur diese Layouts unterstützen echte Richtungsparameter
const ORIENTATION_SUPPORTED = new Set(['dagre', 'klay']);

// kompakte UI-Klassen
const btn =
  'inline-flex items-center justify-center h-8 px-2 rounded-lg ' +
  'border border-gray-300/60 bg-white text-[12px] leading-none ' +
  'hover:bg-gray-50 active:scale-[0.99] transition ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';
const group =
  'inline-flex gap-1 p-1 bg-white/80 backdrop-blur rounded-lg ' +
  'border border-gray-200 shadow-sm';
const select =
  'h-8 pl-2 pr-7 rounded-lg bg-white text-[12px] border-0 outline-none ' +
  'focus:outline-none focus:ring-0 appearance-none';

function orientationCapable(name: string) {
  return ORIENTATION_SUPPORTED.has(name);
}

// Falls keine Node den Key 'cluster' hat, bilden wir Fallback-Cluster:
function buildCiseClusters(cy: any): string[][] | undefined {
  const nodes = cy.nodes();
  const hasClusterKey = nodes.some((n: any) => {
    const v = n.data('cluster');
    return v !== undefined && v !== null && String(v) !== '';
  });
  if (hasClusterKey) return undefined;

  const comps = cy.elements().components();
  const clusters = comps.map((c: any) => c.nodes().map((n: any) => n.id())).filter((arr: string[]) => arr.length > 0);

  if (clusters.length > 0) return clusters;
  return [nodes.map((n: any) => n.id())];
}

// Zentrale Layout-Anwendung (switch/case) mit Defaults je Engine
function applyLayout(cy: any, name: string, orientation: Orientation) {
  if (!cy) return;

  const common: any = { animate: true, animationDuration: 400, fit: true, spacingFactor: 1.1 };

  switch (name) {
    case 'dagre':
      cy.layout({ name, ...common, rankDir: orientation }).run();
      return;

    case 'klay': {
      const dirMap: Record<Orientation, 'RIGHT' | 'LEFT' | 'DOWN' | 'UP'> =
        { LR: 'RIGHT', RL: 'LEFT', TB: 'DOWN', BT: 'UP' };
      cy.layout({
        name,
        ...common,
        klay: {
          direction: dirMap[orientation],
          edgeRouting: 'ORTHOGONAL',
          nodePlacement: 'BRANDES_KOEPF',
          spacing: 28,
        },
      }).run();
      return;
    }

    case 'breadthfirst':
      cy.layout({ name, ...common, directed: true }).run();
      return;

    case 'cose':
      cy.layout({ name, ...common }).run();
      return;

    case 'cose-bilkent':
      cy.layout({ name, ...common, quality: 'default' }).run();
      return;

    case 'cola':
      cy.layout({ name, ...common, avoidOverlap: true }).run();
      return;

    case 'euler':
      cy.layout({ name, ...common }).run();
      return;

    case 'fcose':
      cy.layout({ name, ...common, quality: 'default', randomize: true }).run();
      return;

    case 'cise': {
      const clusters = buildCiseClusters(cy);
      cy.layout({
        name,
        ...common,
        animate: 'end',
        padding: 30,
        allowNodesInsideCircle: false,
        nodeSeparation: 20,
        idealInterClusterEdgeLengthCoefficient: 1.6,
        clusters: clusters ?? ((n: any) => {
          const v = n.data('cluster');
          return (v === undefined || v === null || String(v) === '') ? -1 : v;
        }),
      }).run();
      return;
    }

    default:
      cy.layout({ name: 'dagre', ...common, rankDir: orientation }).run();
      return;
  }
}

const GraphLayoutControls: React.FC<Props> = ({
  cy,
  initialLayoutName,
  currentLayoutName,
  onLayoutChange,
}) => {
  // interner Fallback-State (unchanged)
  const [layoutName, setLayoutName] = useState<string>(initialLayoutName || 'dagre');
  const [orientation, setOrientation] = useState<Orientation>('LR');

  // wenn controlled, nehmen wir die Vorgabe, sonst den lokalen State
  const selectedLayoutName = currentLayoutName ?? layoutName;

  const canOrient = useMemo(() => orientationCapable(selectedLayoutName), [selectedLayoutName]);

  const runOrientation = (o: Orientation) => {
    setOrientation(o);
    if (cy && canOrient) applyLayout(cy, selectedLayoutName, o);
  };

  const apply = () => {
    if (cy) applyLayout(cy, selectedLayoutName, orientation);
  };

  const fit = () => {
    if (!cy) return;
    cy.fit(undefined, 16);
  };

  const handleLayoutSelect = (val: string) => {
    if (onLayoutChange) {
      onLayoutChange(val);     // nach oben melden (zentraler State)
    } else {
      setLayoutName(val);      // un-controlled weiter möglich
    }
  };

  return (
    <div className="w-full h-full flex items-center gap-2 text-[12px]">
      {/* Layout + Apply + Fit */}
      <div className={group} role="group" aria-label="Layout">
        <div className="relative">
          <select
            className={select}
            value={selectedLayoutName}
            onChange={(e) => handleLayoutSelect(e.target.value)}
            title="Layout-Engine wählen"
          >
            {AVAILABLE_LAYOUTS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          {/* Caret */}
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[14px]">
            ▾
          </span>
        </div>

        <button className={btn} onClick={apply} title="Layout anwenden">
          Apply
        </button>
        <br />
        <button
          className={`${btn} w-8 px-0`}
          onClick={fit}
          title="Fit content"
          aria-label="Fit content"
        >
          <span className="e-icons e-zoom-to-fit text-[14px]" />
        </button>
        <br />
        {ORIENTATIONS.map((o) => (
          <button
            key={o.key}
            className={`${btn} w-8 px-0 ${orientation === o.key ? 'bg-gray-100' : ''}`}
            title={canOrient ? o.title : 'Dieses Layout unterstützt keine Richtung'}
            onClick={() => runOrientation(o.key)}
            disabled={!cy || !canOrient}
            aria-pressed={orientation === o.key}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GraphLayoutControls;
